function getUA() {
  if (typeof navigator === "undefined") return "";
  return String(navigator.userAgent || "");
}

function readEnv(key, fallback = "") {
  const env = typeof process !== "undefined" && process?.env ? process.env : {};
  return String(env[key] ?? fallback).trim();
}

function isServiceWorkerEnabledByEnv() {
  const explicit = readEnv("REACT_APP_ENABLE_SW", "");
  if (explicit) {
    const normalized = explicit.toLowerCase();
    if (["0", "false", "no", "off"].includes(normalized)) return false;
    if (["1", "true", "yes", "on"].includes(normalized)) return true;
  }

  if (readEnv("NODE_ENV", "development") === "production") return true;

  // Safety net for mis-deployed CRA builds: when a compiled bundle is served from
  // a real HTTPS production domain, SW must not be treated as dev-disabled.
  if (typeof window !== "undefined") {
    const host = String(window.location.hostname || "").toLowerCase();
    const isLocal = host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0" || host.endsWith(".local");
    if (!isLocal && window.isSecureContext) return true;
  }

  return false;
}

function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = getUA();
  const iOSRegex = /iPad|iPhone|iPod/i;
  const isModernIPad =
    navigator.platform === "MacIntel" && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return iOSRegex.test(ua) || isModernIPad;
}

function isAndroidDevice() {
  return /Android/i.test(getUA());
}

function isFirefoxBrowser() {
  return /Firefox\//i.test(getUA());
}

function isSafariBrowser() {
  const ua = getUA();
  return /Safari/i.test(ua) && !/Chrome|Chromium|CriOS|FxiOS|EdgiOS|EdgA|OPR|OPiOS/i.test(ua);
}

function isChromiumBrowser() {
  const ua = getUA();
  return /Chrome|Chromium|CriOS|EdgA|EdgiOS|Edg\//i.test(ua);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mediaStandalone = Boolean(window.matchMedia?.("(display-mode: standalone)")?.matches);
  const iosStandalone = Boolean(window.navigator?.standalone);
  return mediaStandalone || iosStandalone;
}

function isSecureForWebApis() {
  if (typeof window === "undefined") return false;
  if (window.isSecureContext) return true;
  const h = String(window.location.hostname || "").toLowerCase();
  return h === "localhost" || h === "127.0.0.1";
}

function resolvePushUnavailableReason(caps) {
  if (!caps.secureContext) return "Push доступны только по HTTPS или на localhost";
  if (!caps.rawCanServiceWorker) return "Service Worker не поддерживается этим браузером";
  if (!caps.serviceWorkerRuntimeEnabled) {
    return "Service Worker отключён настройкой REACT_APP_ENABLE_SW=false или страница открыта не по HTTPS";
  }
  if (!caps.canNotificationApi) return "Notifications API не поддерживается этим браузером";
  if (!caps.canPushManager) {
    if (caps.isIOS && caps.needsInstallForIOSPush) {
      return "На iPhone/iPad добавьте сайт на экран «Домой» и откройте как приложение, затем включите Push";
    }
    return "Push API недоступен в этом браузере, режиме или контексте";
  }
  return "";
}

export function detectPwaCapabilities() {
  const rawCanServiceWorker = typeof navigator !== "undefined" && "serviceWorker" in navigator;
  const canPushManager = typeof window !== "undefined" && "PushManager" in window;
  const canNotificationApi = typeof window !== "undefined" && "Notification" in window;
  const secureContext = isSecureForWebApis();
  const serviceWorkerRuntimeEnabled = isServiceWorkerEnabledByEnv();
  const ios = isIOSDevice();
  const standalone = isStandalone();
  const canServiceWorker = rawCanServiceWorker && serviceWorkerRuntimeEnabled && secureContext;
  const canPushApi = canPushManager && canNotificationApi;
  const canPush = canServiceWorker && canPushApi;

  const caps = {
    isIOS: ios,
    isAndroid: isAndroidDevice(),
    isFirefox: isFirefoxBrowser(),
    isSafari: isSafariBrowser(),
    isChromium: isChromiumBrowser(),
    isStandalone: standalone,
    secureContext,
    rawCanServiceWorker,
    serviceWorkerRuntimeEnabled,
    canServiceWorker,
    canPushManager,
    canNotificationApi,
    canPushApi,
    canPush,
    needsInstallForIOSPush: ios && !standalone,
  };

  return {
    ...caps,
    pushUnavailableReason: resolvePushUnavailableReason(caps),
  };
}
